import React from 'react';

import { CircleHelp, FileText, Sheet } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { KimMode } from '@/types';

const EXTRACTION_MAX_DOCUMENTS = 25000;

interface ModeSelectorProps {
  mode: KimMode;
  setMode: (mode: KimMode) => void;
  setPlaceholder: (placeholder: string) => void;
  documentCount?: number;
}

export function ModeSelector({ mode, setMode, setPlaceholder, documentCount }: ModeSelectorProps) {
  const isExtractionDisabled = documentCount !== undefined && documentCount > EXTRACTION_MAX_DOCUMENTS;

  const extractionTooltipMessage =
    'Create a subset of fewer than 25,000 documents to analyse using the extraction flow. Extraction runs your prompt across all documents. For matters with large datasets, we recommend focussing your query on smaller subsets in order to retrieve the best information.';

  return (
    <div className="mt-2 grid w-full grid-cols-3 gap-4">
      <ModeOption
        mode={KimMode.QuestionFlow}
        currentMode={mode}
        icon={<CircleHelp className="h-4 w-4" />}
        title="Question"
        description="Ask a question and get an answer."
        onClick={() => {
          setMode(KimMode.QuestionFlow);
          setPlaceholder('Tell me about X...');
        }}
      />
      <TooltipProvider>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <span className={isExtractionDisabled ? 'cursor-not-allowed' : ''}>
              <ModeOption
                mode={KimMode.ExtractionFlow}
                currentMode={mode}
                icon={<Sheet className="h-4 w-4" />}
                title="Extraction"
                description="Extract information/entities into a report."
                disabled={isExtractionDisabled}
                onClick={() => {
                  setMode(KimMode.ExtractionFlow);
                  setPlaceholder('Extract all mentions of Y into a report...');
                }}
              />
            </span>
          </TooltipTrigger>
          {isExtractionDisabled && (
            <TooltipContent className="max-w-xs">
              <p>{extractionTooltipMessage}</p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
      <ModeOption
        mode={KimMode.DraftFlow}
        currentMode={mode}
        icon={<FileText className="h-4 w-4" />}
        title="Draft"
        description="Create a draft document backed by facts."
        onClick={() => {
          setMode(KimMode.DraftFlow);
          setPlaceholder('Prepare me a draft letter of [X] about [Y]...');
        }}
      />
    </div>
  );
}

interface ModeOptionProps {
  mode: string;
  currentMode: KimMode;
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}

const ModeOption: React.FC<ModeOptionProps> = ({
  mode,
  currentMode,
  icon,
  title,
  description,
  onClick,
  disabled = false,
}) => {
  const isActive = mode === currentMode;
  const baseClasses = 'relative p-4 rounded-lg border transition-all duration-300 ease-in-out transform w-full';
  const activeClasses = isActive ? 'border-blue-500 bg-blue-50 scale-[1.02]' : 'border-gray-200 ';
  const hoverClasses = disabled ? '' : 'hover:scale-[1.02]';
  const disabledClasses = disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer';

  return (
    <button
      className={`${baseClasses} ${activeClasses} ${hoverClasses} ${disabledClasses}`}
      onClick={onClick}
      disabled={disabled}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <div className={`transition-transform duration-300 ${isActive ? 'text-blue-500' : 'text-gray-600'}`}>
            {icon}
          </div>
          <h3
            className={`text-sm font-medium transition-colors duration-300 ${isActive ? 'text-blue-700' : 'text-gray-900'}`}
          >
            {title}
          </h3>
        </div>
        <p className="text-xs text-gray-600 transition-opacity duration-300">{description}</p>
      </div>
      {isActive && <div className="absolute -top-1 -right-1 size-2.5 rounded-full bg-blue-500" />}
    </button>
  );
};
